import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { AgGridReact } from "ag-grid-react";
import Swal from "sweetalert2";
import {
  Trash2,
  Edit,
  PlusCircle,
  RotateCcw,
  ChevronUp,
} from "feather-icons-react/build/IconComponents";
import { setToogleHeader } from "../../core/redux/action";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import { secureExportToExcel } from "../../utils/secureExcelUtils";
import { fetchProductOrderList, deleteProductOrder } from "../../core/api/products";
import OrderDetailsView from "./orderdetailsview";
import { message, Button, Badge } from "antd";
import { getProfile } from "../../core/api/auth";

const ProductOrdersList = () => {
  const [orders, setOrders] = useState([]);
  const selectedClients = useSelector((state) => state.selectedClients);
  const [clientId, setClientId] = useState(null);
  const [isOrderDetailsVisible, setOrderDetailsVisible] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(0);
  const sidebarRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5000);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const gridRef = useRef(null);
  const gridApiRef = useRef(null);
  // Track in-flight request key to prevent duplicate API calls
  const pendingRequestRef = useRef(null);
  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  // Permissions - you may need to implement these based on your user roles

  const renderExcelTooltip = (props) => (
    <Tooltip id="excel-tooltip" {...props}>
      Excel
    </Tooltip>
  );

  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );

  const renderCollapseTooltip = (props) => (
    <Tooltip id="collapse-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  useEffect(() => {
    fetchOrderData();
  }, [selectedClients]);

  const fetchOrderData = async (page = 1, pageSize = 5000) => {
    // Validate pagination params first
    const validPage = typeof page === "number" && !isNaN(page) ? page : 1;
    const validPageSize = typeof pageSize === "number" && !isNaN(pageSize) ? pageSize : 5000;

    // Build unique key and deduplicate
    const requestKey = `${selectedClients.join(",")}-${validPage}-${validPageSize}`;
    if (pendingRequestRef.current === requestKey || loading) {
      console.log("Skipping duplicate or in-progress product orders request", requestKey);
      return;
    }
    pendingRequestRef.current = requestKey;

    setLoading(true);
    // Show loading overlay in the grid if it's available
    if (gridApiRef.current && gridApiRef.current.api) {
      gridApiRef.current.api.showLoadingOverlay();
    }

    const startTime = performance.now();

    try {
      // Make API request with proper pagination parameters
      const response = await fetchProductOrderList(selectedClients, validPage, validPageSize);

      // Process the data - add calculated fields to each item
      const indexedData = response.data.results.map((item, idx) => {
        const startIndex = (validPage - 1) * validPageSize + 1;
        const delivered = item.receivings?.reduce((sum, r) => sum + r.quantity, 0) || 0;
        const pending = Math.max(0, item.quantity - delivered);
        const total =
          item.quantity * item.unit_price + (item.delivery_cost || 0) + (item.label_cost || 0);
        const final_unit_price = item.quantity > 0 ? total / item.quantity : 0;

        return {
          ...item,
          index: startIndex + idx,
          delivered,
          pending,
          total,
          final_unit_price,
        };
      });

      // Update state with processed data
      setOrders(indexedData);

      // Set pagination data from the API response
      setTotalItems(response.data.count);
      setTotalPages(response.data.total_pages);
      setPage(response.data.page);
      setPageSize(response.data.page_size);
      setDataLoaded(true);

      const endTime = performance.now();
      console.log(`Product Orders data fetch took ${endTime - startTime}ms`);
    } catch (error) {
      console.error("Error fetching orders:", error);
      message.error("Failed to load orders. Please try again.");

      // Show no rows overlay if there's an error
      if (gridApiRef.current && gridApiRef.current.api) {
        gridApiRef.current.api.showNoRowsOverlay();
      }
    } finally {
      // Clear pending request flag
      if (pendingRequestRef.current === requestKey) {
        pendingRequestRef.current = null;
      }
      const totalTime = performance.now() - startTime;
      console.log(`Total product orders loading time: ${totalTime.toFixed(2)}ms`);

      setLoading(false);
      // Hide loading overlay
      if (gridApiRef.current && gridApiRef.current.api) {
        gridApiRef.current.api.hideOverlay();
      }
    }
  };

  const handlePageChange = (newPage, newPageSize) => {
    fetchOrderData(newPage, newPageSize);
  };

  const handleCreateOrder = async () => {
    if (selectedClients.length === 0) {
      message.warning("Please select a client.");
      return;
    }

    if (selectedClients.length > 1 || selectedClients[0] === "all") {
      message.warning("Please select one client.");
      return;
    }

    setClientId(selectedClients[0]);
    setCurrentOrderId(null);
    setOrderDetailsVisible(true);
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsVisible(false);
    setCurrentOrderId(null);
  };

  const valueGetter = (params) => {
    return params.data[params.colDef.field];
  };

  const cellRenderer = ({ value }) => {
    if (value === null || value === undefined) {
      return "-";
    }
    return value;
  };

  const dateFormatter = ({ value }) => {
    if (!value) return "-";
    const date = new Date(value);
    return date.toLocaleDateString();
  };

  const priceFormatter = ({ value }) => {
    if (value === null || value === undefined) {
      return "-";
    }
    return `$${parseFloat(value).toFixed(2)}`;
  };

  const actionsRenderer = (params) => {
    return (
      <div className="action-table-data edit-delete-action d-flex gap-1">
        <a
          className="p-1"
          title="Edit Order"
          onClick={() =>
            handleEditOrder(
              params.data.order_id || params.data.product_order_id,
              "Product Order",
              params.data.client_id || params.data.brand_id
            )
          }>
          <Edit className="feather-edit" style={{ width: 16, height: 16 }} />
        </a>
        <a
          className="p-1 confirm-text"
          title="Delete Order"
          onClick={() =>
            handleDeleteOrder(params.data.order_id || params.data.product_order_id, "Product Order")
          }>
          <Trash2 className="feather-delete" style={{ width: 16, height: 16 }} />
        </a>
      </div>
    );
  };

  const receivingsStyles = {
    container: {
      display: "flex",
      alignItems: "center",
      marginBottom: "4px",
    },
    label: {
      marginLeft: "8px",
      fontSize: "12px",
    },
    qty: {
      color: "#495057",
      fontWeight: "500",
    },
  };

  const receivingsRenderer = (params) => {
    if (!params.data.receivings || params.data.receivings.length === 0) {
      return "None";
    }

    const count = params.data.receivings.length;

    return (
      <div className="receivings-container" style={{ padding: "5px 0" }}>
        <OverlayTrigger
          trigger="click"
          rootClose
          placement="auto"
          container={document.body}
          popperConfig={{
            modifiers: [
              {
                name: "preventOverflow",
                options: {
                  boundary: document.body,
                },
              },
            ],
          }}
          overlay={
            <div
              className="custom-receivings-overlay"
              style={{
                backgroundColor: "#ffffff",
                border: "1px solid #d9d9d9",
                borderRadius: "4px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                padding: "16px",
                maxHeight: "600px",
                overflowY: "auto",
                zIndex: 1050,
                minWidth: "400px",
              }}>
              <div style={{ marginBottom: "12px" }}>
                <h6
                  style={{
                    borderBottom: "1px solid #f0f0f0",
                    paddingBottom: "8px",
                    marginBottom: "12px",
                    fontWeight: "600",
                    color: "#1890ff",
                  }}>
                  Receivings for {params.data.product_name}{" "}
                  {params.data.product_variation_desc
                    ? `(${params.data.product_variation_desc})`
                    : ""}
                </h6>

                <div className="card mb-3">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">Order Information</h6>
                  </div>
                  <div className="card-body py-2">
                    <div className="row">
                      <div className="col-md-6">
                        <p className="mb-1">
                          <strong>Invoice:</strong> {params.data.invoice_number}
                        </p>
                        <p className="mb-1">
                          <strong>Product:</strong> {params.data.product_name}
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1">
                          <strong>Variation:</strong> {params.data.product_variation_desc}
                        </p>
                        <p className="mb-1">
                          <strong>Quantity:</strong> {params.data.quantity}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <table className="table table-sm table-bordered" style={{ marginBottom: 0 }}>
                  <thead style={{ backgroundColor: "#f5f5f5" }}>
                    <tr>
                      <th style={{ padding: "8px 12px" }}>Date</th>
                      <th style={{ padding: "8px 12px", width: "120px" }}>Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {params.data.receivings.map((receiving, idx) => (
                      <tr key={idx}>
                        <td style={{ padding: "8px 12px" }}>
                          {new Date(receiving.delivery_date).toLocaleDateString()}
                        </td>
                        <td
                          style={{
                            textAlign: "center",
                            padding: "8px 12px",
                            fontWeight: "500",
                          }}>
                          {receiving.quantity} units
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          }>
          <div
            style={receivingsStyles.container}
            className="clickable-badge"
            onClick={(e) => e.stopPropagation()}>
            <Badge
              count={count}
              style={{
                backgroundColor: "#1890ff",
                cursor: "pointer",
              }}
            />
            <span style={{ ...receivingsStyles.label, cursor: "pointer" }}>
              Receiving{count > 1 ? "s" : ""}
            </span>
          </div>
        </OverlayTrigger>
      </div>
    );
  };

  const handleEditOrder = (orderId, orderType, cId) => {
    setClientId(cId);
    setCurrentOrderId(orderId);
    setOrderDetailsVisible(true);
  };

  const handleDeleteOrder = (orderId, orderType) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteProductOrder(orderId);
          Swal.fire("Deleted!", "Your order has been deleted.", "success");
          fetchOrderData(page, pageSize);
        } catch (error) {
          console.error("Error deleting order:", error);
          Swal.fire("Error!", "There was an error deleting the order.", "error");
        }
      }
    });
  };

  const handleSearch = (e) => {
    if (gridApiRef.current) {
      gridApiRef.current.api.setQuickFilter(e.target.value);
    }
    setSearchQuery(e.target.value);
  };

  const exportToExcel = () => {
    if (!orders || orders.length === 0) {
      Swal.fire("No Data", "There is no data to export.", "info");
      return;
    }

    const exportData = orders.map((order) => ({
      "#": order.index,
      Brand: order.brand_name || "-",
      "Invoice Number": order.invoice_number || "-",
      "Invoice Date": order.invoice_date ? new Date(order.invoice_date).toLocaleDateString() : "-",
      Product: order.product_name || "-",
      Variation: order.product_variation_desc || "-",
      Supplier: order.supplier_name || "-",
      Warehouse: order.warehouse_name || "-",
      Status: order.status_name || "-",
      Quantity: order.quantity || 0,
      Delivered: order.delivered || 0,
      Pending: order.pending || 0,
      Total: order.total ? `$${parseFloat(order.total).toFixed(2)}` : "$0.00",
      "Unit Price": order.final_unit_price
        ? `$${parseFloat(order.final_unit_price).toFixed(2)}`
        : "$0.00",
    }));

    secureExportToExcel(exportData, "Product_Orders_List");
  };

  const columnDefs = [
    {
      headerName: "#",
      field: "index",
      valueGetter: valueGetter,
      width: 70,
      minWidth: 70,
      maxWidth: 80,
      sortable: false,
      filter: false,
      resizable: true,
    },
    {
      headerName: "Brand",
      field: "brand_name",
      valueGetter: valueGetter,
      cellRenderer: cellRenderer,
      width: 160,
      minWidth: 140,
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      headerName: "Invoice Number",
      field: "invoice_number",
      valueGetter: valueGetter,
      cellRenderer: cellRenderer,
      width: 180,
      minWidth: 160,
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      headerName: "Invoice Date",
      field: "invoice_date",
      valueGetter: valueGetter,
      cellRenderer: dateFormatter,
      width: 150,
      minWidth: 140,
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      headerName: "Product",
      field: "product_name",
      valueGetter: valueGetter,
      cellRenderer: cellRenderer,
      width: 200,
      minWidth: 180,
      flex: 1.5,
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      headerName: "Variation",
      field: "product_variation_desc",
      valueGetter: valueGetter,
      cellRenderer: cellRenderer,
      width: 240,
      minWidth: 220,
      flex: 1.5,
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      headerName: "Supplier",
      field: "supplier_name",
      valueGetter: valueGetter,
      cellRenderer: cellRenderer,
      width: 170,
      minWidth: 150,
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      headerName: "Warehouse",
      field: "warehouse_name",
      valueGetter: valueGetter,
      cellRenderer: cellRenderer,
      width: 180,
      minWidth: 160,
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      headerName: "Status",
      field: "status_name",
      valueGetter: valueGetter,
      cellRenderer: cellRenderer,
      width: 150,
      minWidth: 140,
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      headerName: "Quantity",
      field: "quantity",
      valueGetter: valueGetter,
      cellRenderer: cellRenderer,
      width: 130,
      minWidth: 120,
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      headerName: "Delivered",
      field: "delivered",
      valueGetter: valueGetter,
      cellRenderer: cellRenderer,
      width: 130,
      minWidth: 120,
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      headerName: "Pending",
      field: "pending",
      valueGetter: valueGetter,
      cellRenderer: cellRenderer,
      width: 130,
      minWidth: 120,
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      headerName: "Receivings",
      field: "receivings",
      cellRenderer: receivingsRenderer,
      cellStyle: { whiteSpace: "normal" },
      width: 220,
      minWidth: 200,
      flex: 1,
      sortable: false,
      filter: false,
      resizable: true,
    },
    {
      headerName: "Total",
      field: "total",
      valueGetter: valueGetter,
      cellRenderer: priceFormatter,
      width: 150,
      minWidth: 130,
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      headerName: "Unit Price",
      field: "final_unit_price",
      valueGetter: valueGetter,
      cellRenderer: priceFormatter,
      width: 150,
      minWidth: 130,
      sortable: true,
      filter: true,
      resizable: true,
    },
    {
      headerName: "Actions",
      field: "actions",
      cellRenderer: actionsRenderer,
      width: 120,
      minWidth: 100,
      maxWidth: 140,
      sortable: false,
      filter: false,
      resizable: false,
    },
  ];

  return (
    <div className="p-1">
      <div className="page-header">
        <div className="add-item d-flex">
          <div className="page-title">
            <h4>Product Orders List</h4>
          </div>
        </div>
        <ul className="table-top-head">
          <div className="search-set">
            <div className="search-input">
              <input
                type="text"
                placeholder="Search"
                className="form-control form-control-sm formsearch"
                value={searchQuery}
                onChange={handleSearch}
              />
              <Link to="#" className="btn btn-searchset">
                <i data-feather="search" className="feather-search" />
              </Link>
            </div>
          </div>
          <li>
            <OverlayTrigger placement="top" overlay={renderExcelTooltip}>
              <Link to="#" data-bs-toggle="tooltip" data-bs-placement="top" onClick={exportToExcel}>
                <ImageWithBasePath src="assets/img/icons/xls.svg" alt="img" />
              </Link>
            </OverlayTrigger>
          </li>
          <li>
            <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
              <Link
                to="#"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                onClick={() => fetchOrderData(page, pageSize)}>
                <RotateCcw />
              </Link>
            </OverlayTrigger>
          </li>
          <li>
            <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
              <Link
                to="#"
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                id="collapse-header"
                className={data ? "active" : ""}
                onClick={(e) => {
                  e.preventDefault();
                  dispatch(setToogleHeader(!data));
                }}>
                <ChevronUp />
              </Link>
            </OverlayTrigger>
          </li>
        </ul>

        <div className="page-btn d-flex align-items-center justify-content-between">
          <button className="btn btn-added" onClick={handleCreateOrder}>
            <PlusCircle className="me-2 iconsize" />
            Create New Product Order
          </button>
        </div>
      </div>
      <div className="">
        <div className="table-responsive">
          <div
            className="ag-theme-quartz position-relative"
            style={{ height: "70vh", width: "100%" }}>
            {/* Custom Loading Overlay */}
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                  zIndex: 1000,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}>
                <div
                  className="spinner-border text-primary"
                  role="status"
                  style={{ width: "3rem", height: "3rem" }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div style={{ marginTop: "15px", fontWeight: "bold" }}>Loading orders...</div>
              </div>
            )}
            <AgGridReact
              ref={gridRef}
              onGridReady={(params) => {
                gridApiRef.current = params;
                params.api.sizeColumnsToFit();
              }}
              rowData={orders}
              columnDefs={columnDefs}
              defaultColDef={{
                flex: 1,
                minWidth: 100,
                sortable: true,
                filter: true,
                resizable: true,
                wrapText: true,
              }}
              suppressPaginationPanel={true}
              suppressRowTransform={true}
              pagination={true}
              paginationPageSize={pageSize}
              onFirstDataRendered={(params) => {
                params.api.sizeColumnsToFit();
              }}
            />
          </div>

          {/* Custom Pagination Footer */}
          <div className="row mt-3">
            <div className="col-md-6">
              <div className="pagination-info">
                Showing {orders.length > 0 ? (page - 1) * pageSize + 1 : 0} to{" "}
                {Math.min(page * pageSize, totalItems)} of {totalItems} entries
              </div>
            </div>
            <div className="col-md-6">
              <div className="d-flex justify-content-end gap-2">
                <div className="d-flex align-items-center me-3">
                  <span className="me-2">Show</span>
                  <select
                    className="form-select form-select-sm"
                    value={pageSize}
                    onChange={(e) => handlePageChange(1, parseInt(e.target.value))}>
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="500">500</option>
                    <option value="1000">1000</option>
                    <option value="2000">2000</option>
                    <option value="5000">5000</option>
                  </select>
                  <span className="ms-2">entries</span>
                </div>

                <div className="d-flex gap-1">
                  <button
                    onClick={() => handlePageChange(1, pageSize)}
                    disabled={page === 1}
                    style={{
                      padding: "5px 10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: page === 1 ? "not-allowed" : "pointer",
                      opacity: page === 1 ? 0.5 : 1,
                    }}>
                    First
                  </button>
                  <button
                    onClick={() => handlePageChange(page - 1, pageSize)}
                    disabled={page === 1}
                    style={{
                      padding: "5px 10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: page === 1 ? "not-allowed" : "pointer",
                      opacity: page === 1 ? 0.5 : 1,
                    }}>
                    Prev
                  </button>

                  {/* Page numbers */}
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      // If 5 or fewer pages, show all
                      pageNum = i + 1;
                    } else if (page <= 3) {
                      // If near start, show first 5
                      pageNum = i + 1;
                    } else if (page >= totalPages - 2) {
                      // If near end, show last 5
                      pageNum = totalPages - 4 + i;
                    } else {
                      // Otherwise show 2 before and 2 after current
                      pageNum = page - 2 + i;
                    }

                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum, pageSize)}
                        style={{
                          padding: "5px 10px",
                          border: "1px solid #ddd",
                          borderRadius: "4px",
                          backgroundColor: page === pageNum ? "#007bff" : "white",
                          color: page === pageNum ? "white" : "black",
                        }}>
                        {pageNum}
                      </button>
                    );
                  })}

                  <button
                    onClick={() => handlePageChange(page + 1, pageSize)}
                    disabled={page === totalPages}
                    style={{
                      padding: "5px 10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: page === totalPages ? "not-allowed" : "pointer",
                      opacity: page === totalPages ? 0.5 : 1,
                    }}>
                    Next
                  </button>
                  <button
                    onClick={() => handlePageChange(totalPages, pageSize)}
                    disabled={page === totalPages}
                    style={{
                      padding: "5px 10px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      cursor: page === totalPages ? "not-allowed" : "pointer",
                      opacity: page === totalPages ? 0.5 : 1,
                    }}>
                    Last
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {clientId && (
        <OrderDetailsView
          ref={sidebarRef}
          isVisible={isOrderDetailsVisible}
          onClose={handleCloseOrderDetails}
          orderId={currentOrderId}
          onResize={setSidebarWidth}
          setOrderId={setCurrentOrderId}
          fetchOrderList={fetchOrderData}
          clientId={clientId}
        />
      )}
    </div>
  );
};

export default ProductOrdersList;
